import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";

const columnMap = {
  establishments: [
    { key: "name", label: "Name" },
    { key: "email", label: "Email" },
    { key: "contact", label: "Contact" },
    { key: "role", label: "Role" },
    { key: "usersCount", label: "Users" },
    { key: "clientsCount", label: "Clients" },
    { key: "supervisorsCount", label: "Supervisors" },
  ],
  clients: [
    { key: "name", label: "Name" },
    { key: "email", label: "Email" },
    { key: "contact", label: "Contact" },
    { key: "statusText", label: "Status" },
    { key: "establishmentName", label: "Establishment" },
  ],
  supervisors: [
    { key: "name", label: "Name" },
    { key: "email", label: "Email" },
    { key: "contact", label: "Contact" },
    { key: "statusText", label: "Status" },
    { key: "establishmentName", label: "Establishment" },
  ],
  users: [
    { key: "full_Name", label: "Name" },
    { key: "email", label: "Email" },
    { key: "contact", label: "Contact" },
    { key: "employeeIdText", label: "Employee ID" },
    { key: "kycText", label: "KYC" },
    { key: "activeStatusText", label: "Status" },
    { key: "establishmentName", label: "Establishment" },
  ],
  "active-users": [
    { key: "full_Name", label: "Name" },
    { key: "email", label: "Email" },
    { key: "contact", label: "Contact" },
    { key: "employeeIdText", label: "Employee ID" },
    { key: "activeStatusText", label: "Status" },
    { key: "establishmentName", label: "Establishment" },
  ],
  "kyc-verified": [
    { key: "full_Name", label: "Name" },
    { key: "email", label: "Email" },
    { key: "contact", label: "Contact" },
    { key: "employeeIdText", label: "Employee ID" },
    { key: "kycText", label: "KYC" },
    { key: "establishmentName", label: "Establishment" },
  ],
  "pending-kyc": [
    { key: "full_Name", label: "Name" },
    { key: "email", label: "Email" },
    { key: "contact", label: "Contact" },
    { key: "employeeIdText", label: "Employee ID" },
    { key: "kycText", label: "KYC" },
    { key: "establishmentName", label: "Establishment" },
  ],
  "pending-wages": [
    { key: "full_Name", label: "Name" },
    { key: "email", label: "Email" },
    { key: "contact", label: "Contact" },
    { key: "employeeIdText", label: "Employee ID" },
    { key: "wagesStatusText", label: "Wages" },
    { key: "establishmentName", label: "Establishment" },
  ],
  "pending-pf-esic": [
    { key: "full_Name", label: "Name" },
    { key: "email", label: "Email" },
    { key: "contact", label: "Contact" },
    { key: "employeeIdText", label: "Employee ID" },
    { key: "pfEsicStatusText", label: "PF/ESIC" },
    { key: "establishmentName", label: "Establishment" },
  ],
};

const normalizeRecord = (type, item) => ({
  ...item,
  usersCount: item?.users?.length || 0,
  clientsCount: item?.clients?.length || 0,
  supervisorsCount: item?.supervisors?.length || 0,
  establishmentName: item?.establisment?.name || "—",
  employeeIdText: item?.employeeId ? `CMPL${item.employeeId}` : "—",
  kycText: item?.kyc ? "Verified" : "Pending",
  activeStatusText: item?.active_user_status ? "Active" : "Inactive",
  statusText: item?.status === false ? "Inactive" : "Active",
  wagesStatusText: item?.wages_status ? "Completed" : "Pending",
  pfEsicStatusText: item?.pf_esic_status ? "Completed" : "Pending",
});

const downloadCsv = (filename, rows, columns) => {
  const escapeCell = (value) => {
    const text = String(value ?? "");
    return `"${text.replace(/"/g, '""')}"`;
  };

  const csv = [
    columns.map((column) => escapeCell(column.label)).join(","),
    ...rows.map((row) =>
      columns.map((column) => escapeCell(row[column.key])).join(",")
    ),
  ].join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const SuperAdminRecords = () => {
  const { type } = useParams();
  const token = localStorage.getItem("token");
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);

  const columns = columnMap[type] || [];

  const rows = useMemo(
    () => (payload?.records || []).map((item) => normalizeRecord(type, item)),
    [payload, type]
  );

  useEffect(() => {
    const fetchRecords = async () => {
      try {
        setLoading(true);
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/super-admin/records/${type}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setPayload(res.data);
      } catch (error) {
        toast.error(
          error?.response?.data?.message || "Unable to load record list"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchRecords();
  }, [type, token]);

  return (
    <div className="container-fluid py-4">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
        <div>
          <h2 className="mb-1">{payload?.title || "Records"}</h2>
          <p className="text-muted mb-0">
            {loading ? "Loading..." : `${payload?.count || 0} records found`}
          </p>
        </div>
        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-success"
            disabled={loading || !rows.length}
            onClick={() =>
              downloadCsv(
                `${type || "records"}.csv`,
                rows,
                columns
              )
            }
          >
            Export CSV
          </button>
          <Link to="/superadmin-dashboard" className="btn btn-outline-primary">
            Back To Dashboard
          </Link>
        </div>
      </div>

      <div className="card shadow-sm border-0">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-striped table-hover align-middle">
              <thead>
                <tr>
                  {columns.map((column) => (
                    <th key={column.key}>{column.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {!loading && rows.length > 0 ? (
                  rows.map((row) => (
                    <tr key={row._id}>
                      {columns.map((column) => (
                        <td key={`${row._id}-${column.key}`}>
                          {row[column.key] || "—"}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={Math.max(columns.length, 1)}
                      className="text-center text-muted py-4"
                    >
                      {loading ? "Loading records..." : "No records available."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminRecords;
